import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import type { Release } from "@typings/entities/releases";
import { useNavigate } from "react-router-dom";
import { prepareReleasesTable } from "@utils/translator/releases";
import ReleasesStatus from "./ReleasesStatus";
import type { ReleasesFilters } from "@services/releases/list/type";
import { useReleasesData } from "@hooks/releases/useReleasesData";
import { ReleasesColumns } from "@typings/entities/releases/table";
import { useExportReleases } from "@hooks/exports/useExportReleases";
import { usePermissions } from "@hooks/usePermissions";

function TableView({ appliedFilters }: { appliedFilters: ReleasesFilters }) {
  const { openModal } = usePage();
  const { handleExport, isLoading: isExporting } = useExportReleases();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const {
    releases,
    isLoading,
    totalItems,
    rowsPerPage,
    page,
    handlePageChange,
    handleRowsPerPageChange,
  } = useReleasesData("table", appliedFilters);

  const processedReleases = prepareReleasesTable(releases);

  const heads = ReleasesColumns;

  const handleView = (release: Release) => {
    navigate(`/managing-releases/${release.id}`);
  };

  const handleExportRelease = (filters: ReleasesFilters) => async () => {
    handleExport(filters);
  };

  const canCreateRelease = can("create", "releases");

  return (
    <div className="w-full">
      <div className="w-full mt-6 flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
        <TableHeader.Root>
          {canCreateRelease && (
            <TableHeader.Button
              customVariant="secondary"
              startIcon={<AddIcon />}
              onClick={() => openModal("newRelease")}
            >
              Nova Soltura
            </TableHeader.Button>
          )}

          <TableHeader.Button
            customVariant="dark"
            onClick={handleExportRelease(appliedFilters)}
            disabled={isExporting}
          >
            {isExporting ? "Exportando..." : "Exportar .csv"}
          </TableHeader.Button>
        </TableHeader.Root>

        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions={true} />
          <Table.Body
            rows={processedReleases}
            columns={heads.map((head) => head.id)}
            columnFormatters={{ status: ReleasesStatus }}
            actions={["view"]}
            onView={handleView}
          />
          <Table.Footer
            colSpan={heads.length}
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>
    </div>
  );
}

export default TableView;
